import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  showModal,
  confirmModal,
  cancelModal,
} from '@store/actions/commonAction';
import SatNumberKeypad from '@components/common/satUI/SatNumberKeypad';
import SatValidationErrMsg from '@components/common/satUI/SatValidationErrMsg';
import SatButton from '@components/common/satUI/SatButton';

import { isMobile, setReceiveKeyEventOnOff } from '@common/utils/device';
import { toDateNumber, toPhoneNumber } from '@common/utils';

const SatInput = forwardRef((props, ref) => {
  const {
    value = '',
    displayValue, // 실제 value와 다르게 보여주고 싶은 값이 있는 경우 사용
    type = 'text', // text(default), html(HTML), number(숫자), currency(화폐), percent(퍼센트), date(날짜), search(직접입력 검색)
    id = '',
    label = '', // 라벨
    size, // Input 크기 - 'x-small', 'small'
    width,
    keyPadSuffix,
    focusLabel = '', // 포커스 이벤트 시 라벨명을 변경하고 싶은 경우
    numberPadTitle, // Number Pad Title (없는 경우, As-is 로직대로, focusLabel => label => placeholder 순으로 적용)
    placeholder = '',
    border = false, // 로그인 & 가입설계동의 & 전자청약 1단계에 사용되는 전체border 있는 case
    readOnly = false, // 읽기전용
    noFocus = false, // Input에 Focus 안가게 할 지 여부 (기본 스타일 유지된 readOnly)
    noSuffix = false, // suffix 비노출 여부
    prefix,
    suffix,
    outerSuffix,
    maxLength,
    minValue, // type === 'date' 일 때 최소값 (선택가능한 가장 이른 날짜)
    maxValue, // type === 'number', 'currency', 'percent' 일 때 최대값
    format, // Validation 체크용 format
    dateLength = 8, // 날짜 길이 (8자리/6자리)
    splitter = '.', // 날짜의 구분자
    useRecommendBtn = '', // type currency에서 추천 가입금액 버튼 사용여부
    currencyType = 'W', // W(원화), D(달러)
    useDot = false, // 숫자키패드(숫자, 화폐)시 소수점 표기를 위해 dot버튼 사용여부()
    decimalPoint = 2, // 소수점 자릿수
    useMobile = false, // 숫자키패드시 010표기 사용여부 & 전화번호 형식표기(xxx-xxxx-xxxx) maxLength = 11
    useDate = false, // 날짜형식표기(YYYY.MM.DD) => maxLength = 8
    isRandomPad = false, // 숫자키패드 시, 숫자 키패드 랜덤으로 섞을 지 여부
    isPrivacy = false, // 노출 값 마스킹 여부
    isShowFirstLetter = false, // 마스킹 시, 첫 번째 값 노출 여부
    validRule = [],
    validGuide = { fixed: false, guideMsg: '' },
    customStyleClass = '', // .input-wrap 에 추가되는 커스텀 클래스
    customInputClass = '', // <input> 태그에 추가되는 커스텀 클래스
    dataIndex,
    textAlign = '',
    disabled = false,
    forceOnClick = false, // readOnly 여도 span 클릭시 onClick 이벤트 처리
    onClick = undefined,
    onBlur = undefined,
    onPressEnter = undefined,
    onChange = undefined,
    onDelete,
    edit = false,
    required = false,
  } = props;

  const inputRef = useRef();
  const dispatch = useDispatch();

  const [isFocused, setIsFocused] = useState(false);
  const [validErr, setValidErr] = useState({
    visible: false,
    errMsg: '',
    type: 'error',
  });
  const [validGuideMsg, setValidGuideMsg] = useState([]);

  useImperativeHandle(ref, () => ({
    // 현재 validation 상태 가져오기
    getValidStatus() {
      return !validErr.visible;
    },
    // 필수값 validation 수행
    isValidCheck() {
      const requiredRule = validRule.find(rule => rule.required);
      if (requiredRule && value === '') {
        setValidErr({
          visible: true,
          errMsg: requiredRule.errMsg,
          type: 'error',
        });

        return false;
      } else {
        return !isValid(value).visible;
      }
    },
    onFocus() {
      setIsFocused(true);
      // if (type !== 'text' && type !== 'search') handleInputClick();
    },
    onBlur() {
      inputRef?.current?.blur();
      // if (type === 'text' || type === 'search') inputRef.current?.blur();
      // setIsFocused(false);
    },
    setValidCheckMsg(msg) {
      if (msg !== '') {
        setValidGuideMsg(prev => {
          return prev.concat({ guideMsg: msg, fixed: true, type: 'check' });
        });
      } else {
        setValidErr({ visible: false });
      }
    },
  }));

  useEffect(() => {
    if (isFocused) inputRef.current?.focus();
  }, [isFocused]);

  useEffect(() => {
    return () => setIsFocused(false);
  }, []);

  useEffect(() => {
    if (type === 'number' || type === 'currency' || type === 'percent') {
      isValid(value);
    }
  }, [value]);

  useEffect(() => {
    if (typeof validGuide === 'object') {
      if (Array.isArray(validGuide)) {
        if (validGuide.filter(guide => guide.guideMsg !== '').length > 0) {
          setValidGuideMsg(validGuide);
        } else {
          // validGuide 없는 경우, 초기화 처리.
          setValidGuideMsg([]);
        }
      } else {
        if (validGuide.guideMsg !== '') {
          setValidGuideMsg([validGuide]);
        }
        // hanjy: 무한로딩 문제로 주석처리
        // else {
        //   // validGuide 없는 경우, 초기화 처리.
        //   setValidGuideMsg([]);
        // }
      }
    }
  }, [validGuide]);

  /**
   * input onChange 이벤트
   * @param {e.target.value} value
   */
  const handleInputChange = value => {
    if (value === '') {
      // if (validErr.visible) setValidErr({ visible: false });
      onChange && onChange(value);
    } else {
      onChange && onChange(value);
    }
  };

  const isValid = data => {
    if (
      validRule.length > 0 &&
      (type === 'currency' ? parseInt(data) !== 0 : data !== '')
    ) {
      const [result] = validRule
        .filter(rule => typeof rule.required === 'undefined')
        .map(rule => {
          return {
            result: rule.rule(data),
            errMsg: rule.errMsg,
          };
        })
        .filter(valid => !valid.result);

      if (result) {
        setValidErr({ visible: true, errMsg: result.errMsg, type: 'error' });
        return { visible: true, errMsg: result.errMsg, type: 'error' };
      } else {
        setValidErr({ visible: false, errMsg: '' });
        return { visible: false, errMsg: '' };
      }
    } else {
      setValidErr({ visible: false, errMsg: '' });
      return { visible: false, errMsg: '' };
    }
  };

  const showNumberKeypad = () => {
    if (type === 'number' || type === 'currency' || type === 'percent') {
      if (isMobile.any()) {
        window.addEventListener(
          'setReceiveKeyEventOnOffCallBack',
          setReceiveKeyEventOnOffCallBack,
          false,
        );
        setReceiveKeyEventOnOff(true);
      }

      dispatch(
        showModal({
          visible: true,
          type: 'slide',
          render: () => (
            <SatNumberKeypad
              ref={keypadRef}
              type={type}
              title={numberPadTitle || focusLabel || label || placeholder}
              numValue={value}
              validGuide={validGuideMsg.filter(guide => !guide.fixed)}
              maxLength={maxLength}
              maxValue={maxValue}
              format={format}
              useRecommendBtn={useRecommendBtn}
              currencyType={currencyType}
              useDot={useDot}
              decimalPoint={decimalPoint}
              useMobile={useMobile}
              useDate={useDate}
              splitter={splitter}
              dateLength={dateLength}
              dataIndex={dataIndex}
              keyPadSuffix={keyPadSuffix}
              isRandomPad={isRandomPad}
              isPrivacy={isPrivacy}
              onOk={handleNumberKeypadComplete}
              onClose={closeNumberKeypad}
            />
          ),
          setVisible: closeNumberKeypad,
        }),
      );
    }
  };

  /**
   * input 포커스 이벤트
   */
  const handleInputFocus = () => {
    // console.log('### handleInputFocus');

    // if ((type === 'text' || type === 'search') && !readOnly) {
    //   setIsFocused(true);
    //   onClick && typeof onClick === 'function' && onClick();
    // }
    setIsFocused(true);
    showNumberKeypad();
  };

  /**
   * input blur 이벤트
   */
  const handleInputBlur = () => {
    // console.log('### handleInputBlur');

    if (
      (type === 'text' ||
        type === 'email' ||
        type === 'password' ||
        type === 'search') &&
      isFocused
    ) {
      setIsFocused(false);
      if (onBlur && typeof onBlur === 'function') {
        onBlur(isValid(value));
      } else {
        isValid(value);
      }
    }

    // @NOTE: Android에서, '다음' 버튼 클릭 시 키보드 사라지지 않는 문제로 아래 코드 추가
    if ($utils.isAndroid) setTimeout(document.activeElement.blur, 100);
  };

  /**
   * input 클릭 이벤트
   */
  const handleInputClick = () => {
    // console.log('### handleInputClick');
    // 이미 Focus 되어 있을 때 클릭 시 Keypad 다시 안올라오는 문제로 추가
    showNumberKeypad();

    onClick && typeof onClick === 'function' && onClick();
  };

  const handleNumberKeypadComplete = value => {
    if (typeof onChange === 'function')
      onChange(value, value !== '0' ? isValid(value) : { visible: false });

    setIsFocused(false);

    dispatch(
      confirmModal(() => {
        if (isMobile.any()) {
          window.removeEventListener(
            'setReceiveKeyEventOnOffCallBack',
            setReceiveKeyEventOnOffCallBack,
            false,
          );
          window.removeEventListener('receiveKeyEvent', receiveKeyEvent, false);

          setReceiveKeyEventOnOff(false);
        }
      }),
    );
  };

  const closeNumberKeypad = () => {
    setIsFocused(false);

    dispatch(
      cancelModal(() => {
        if (isMobile.any()) {
          window.removeEventListener(
            'setReceiveKeyEventOnOffCallBack',
            setReceiveKeyEventOnOffCallBack,
            false,
          );
          window.removeEventListener('receiveKeyEvent', receiveKeyEvent, false);

          setReceiveKeyEventOnOff(false);
        }
      }),
    );
  };

  const keypadRef = useRef();
  const setReceiveKeyEventOnOffCallBack = ({ data: { isOn } }) => {
    if (isOn) {
      window.addEventListener('receiveKeyEvent', receiveKeyEvent, false);
    }
  };

  const receiveKeyEvent = ({ data }) => {
    if (keypadRef && keypadRef.current) {
      keypadRef.current.setInputKeyEvent(data);
    }
  };

  /**
   * 특정 key event
   * @param {event} e
   */
  const handleKeyDown = e => {
    if (type !== 'number' && type !== 'currency' && type !== 'percent') {
      if (e.keyCode === 13) {
        onPressEnter && onPressEnter();
      }
    }
  };

  /**
   * 라벨 가져오기
   * @returns label or focusLabel
   */
  const getLabelText = () => {
    if (label !== '' && focusLabel !== '') {
      if (isFocused || value !== '') {
        return focusLabel;
      }
    }

    return label;
  };

  /**
   *
   * @returns class
   */
  const inputWrapClass = () => {
    let className = 'input-wrap';

    if (id === 'searchNamePop') {
      className += ' input-search';
    } else if (id === 'searchNamePop2') {
      className += ' input-search2';
    } else if (customStyleClass && customStyleClass.includes('inp-search-2')) {
      className += ` ${customStyleClass}`;
      if (disabled) className += ' disabled';
    } else if (customStyleClass && customStyleClass === 'val') {
      className += ' val';
    } else {
      if (!border && type !== 'search') className += ' u-line';
      if (value && value !== '') className += ' val';
      if (isFocused) className += ' focus';
      if (label === '') className += ' line1';
      if (readOnly) className += ' readonly';
      if (validErr.visible && validErr.type === 'error') className += ' error';
      if (customStyleClass !== '') className += ` ${customStyleClass}`;
      if (type === 'search') className += ` inp-sch`;
      if (disabled) className += ' disabled';
      if (size) className += ` ${size}`;
    }

    return className;
  };

  /**
   * input 내부 suffix 영역
   * 기본 input(border아니고 readOnly아니고)일 경우 연필모양이 기본제공
   * @returns
   */
  const defaultSuffix = () => {
    if (!readOnly && !noSuffix) {
      if (
        type === 'text' ||
        type === 'html' ||
        type === 'email' ||
        type === 'search' ||
        (type === 'number' && !outerSuffix)
      ) {
        if (value && value !== '' && type !== 'number') {
          return (
            <SatButton
              styleClass="ico ico-del"
              onClick={() => {
                handleInputChange('');
                if (typeof onDelete === 'function') onDelete();
                inputRef.current?.focus();
              }}
            />
          );
        } else if ((!border && type !== 'search') || edit) {
          return (
            <SatButton
              styleClass="ico ico-edit"
              onClick={() => {
                if (type !== 'number') {
                  inputRef.current?.focus();
                } else {
                  handleInputFocus();
                }
              }}
            />
          );
        }
      }
    }

    return <></>;
  };

  /**
   * input 외부 prefix 영역
   * type이 search일 경우 돋보기 기본 제공
   * @returns
   */
  const defaultPrefix = () => {
    if (type === 'search') {
      return <span className="ico ico-search"></span>;
    } else {
      return '';
    }
  };

  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (useMobile) {
      setInputValue(toPhoneNumber(value));
    } else if (useDate) {
      const dateValue = toDateNumber(value, dateLength, splitter);
      setInputValue(dateValue);
    }
  }, [value]);

  const getDisplayValue = (defaultValue = '') => {
    if (!$utils.isEmptyString(displayValue)) return displayValue;

    let inputDisplayValue =
      useMobile || useDate ? inputValue : value || defaultValue;

    if (isPrivacy) {
      const MASKING_LETTER = '●';

      if (isShowFirstLetter) {
        const firstLetter = inputDisplayValue[0] || '';
        inputDisplayValue = `${firstLetter}${inputDisplayValue
          .substring(1)
          .replace(/\S/g, MASKING_LETTER)}`;
      } else {
        inputDisplayValue = `${inputDisplayValue}`.replace(
          /\S/g,
          MASKING_LETTER,
        );
      }
    }

    return inputDisplayValue;
  };

  return (
    <React.Fragment>
      <div className={inputWrapClass()} style={{ width }}>
        {prefix ? prefix() : defaultPrefix()}

        <div className="input-area" style={{ height: '100%' }}>
          {label !== '' && (
            <label className={`input-tit ${required ? 'required' : ''}`}>
              {getLabelText()}
            </label>
          )}

          {readOnly ? (
            <span
              className={`txt-box ${label === '' ? 'line1' : ''} ${
                textAlign !== '' ? textAlign : ''
              } ${required ? 'required' : ''}`}
              onClick={forceOnClick ? handleInputClick : () => false}
            >
              {/* isPrivacy (마스킹 값) 적용을 위해 getDisplayValue() 사용 */}
              {getDisplayValue(placeholder)}
              {/* {useMobile || useDate ? inputValue : value || placeholder} */}
            </span>
          ) : type === 'html' ? (
            <>
              <div
                className={$classNames('input input-html', {
                  'input-text-left': textAlign === 'left',
                  'input-text-right': textAlign === 'right',
                })}
                onClick={handleInputClick}
                onKeyDown={e => {
                  console.log('contentEditable - e', e);
                  handleKeyDown(e);
                }}
              >
                {$utils.convertToHtml(getDisplayValue())}
              </div>
            </>
          ) : (
            <input
              ref={inputRef}
              className={$classNames('input', customInputClass, {
                'input-text-left': textAlign === 'left',
                'input-text-right': textAlign === 'right',
              })}
              type={
                ['email', 'password', 'date'].includes(type) ? type : 'text'
              }
              id={id}
              // value={useMobile || useDate ? inputValue : value}
              /* isPrivacy (마스킹 값) 적용을 위해 getDisplayValue() 사용 */
              value={getDisplayValue()}
              // value={useMobile || useDate ? inputValue : value}
              disabled={disabled || false}
              readOnly={
                ['number', 'currency', 'percent'].includes(type) ||
                readOnly ||
                noFocus
              }
              maxLength={maxLength}
              min={minValue}
              placeholder={label === '' ? placeholder : ''}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onClick={handleInputClick}
              onKeyDown={handleKeyDown}
              onChange={e => handleInputChange(e.target.value)}
            />
          )}
        </div>

        {suffix ? suffix() : defaultSuffix()}
      </div>

      {outerSuffix && outerSuffix()}
      {validErr.visible ? (
        <SatValidationErrMsg
          visible={validErr.visible}
          type={validErr.type}
          message={validErr.errMsg}
        />
      ) : (
        validGuideMsg.map((guide, index) => {
          return (
            <SatValidationErrMsg
              key={index}
              visible={guide.fixed || (isFocused && guide.guideMsg !== '')}
              type={guide.type || 'info'}
              message={guide.guideMsg}
            />
          );
        })
      )}
    </React.Fragment>
  );
});

export default SatInput;
